import React, { useCallback, useMemo, useRef, useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
  Drawer,
  DrawerPanelContent,
  DrawerContent,
  Stack,
  StackItem,
  DrawerContentBody, Flex, FlexItem
} from '@patternfly/react-core';

import Tree from 'components/GraphTree/Tree';
import TreeFuncs, { updateExpandOnItems, buildTree, NODE_FILTER } from 'components/GraphTree/TreeFuncs';
import { useTreeRoots } from 'components/GraphTree/queries';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';
import TreeSelector from 'components/GraphTree/TreeSelector';
import HelpButton from 'components/Help/HelpButton';
import FormLabel from 'components/Form/FormLabel';

import NodeContext from 'services/Context/NodeContext';

import useAlerts from 'utils/hooks/useAlerts';

import './NodeTreePage.css';
import useTreeQuery from 'components/GraphTree/queries';
import { useTabletOrMobile } from 'utils/hooks/useTabletOrMobile';
import { walkParentPath } from 'utils/nodes';

const nodePathNames = (node) => {
  return _.map(walkParentPath(node), item => item.displayName || item.name);
};

const findNode = _.curry((parentId, nodeId, node) => {
  if (parentId == null) { // handle when we hare sending in tree root as node (when switching tree)
    if (node.id === nodeId) {
      return node;
    }
  }
  if (node.id === parentId) {
    return _.head(_.filter(node.children, { id: nodeId }));
  }
});

// Adds tree selection to page
const NodeTreePage = ({
  children,
  onItemSelected,
  initialRoot: _initialRoot,
  initialExpandedPath,
  onTreeRootChange,
  help,
  panelTitle
}) => {
  // TODO: use error state?
  const nodeContext = useContext(NodeContext);
  const [initialRoot, setInitialRoot] = useState(_initialRoot);
  const [activeItems, setActiveItems] = useState(undefined);
  const [alerts, , addErrorAlert] = useAlerts();
  const [isDrawerExpanded, setIsDrawerExpanded] = useState(true);
  const isTabletOrMobile = useTabletOrMobile();
  const drawerRef = useRef(null);
  const defaultDrawerWidth = localStorage.getItem('drawerWidth') || '150px';
  const [treeId, setTreeId] = useState(_initialRoot);

  const [treeRoots, isLoadingRoots, refreshList] = useTreeRoots({ onError: addErrorAlert });

  // This is done as a state due to some legacy code that needs to be cleaned up.
  // Once this is not as a state the fetching will be much smoother.
  // TODO: remove state handling of query tree id and try to figure out tree id earlier
  const [queryTreeId, setQueryTreeId] = useState(null);

  // TODO: move fetch to customHook and add some of the processing from graphtree as well... (perhaps most?)
  const query = useTreeQuery(queryTreeId, {
    // Only fetch if we have a tree root that matches queryTreeId
    enabled: treeRoots != null && !_.isEmpty(treeRoots) && treeRoots.find(root => root.id === queryTreeId) != null
  });

  const tree = useMemo(() => {
    const originalTree = ((query.isLoading || query.data == null) ? [] : buildTree(query.data)) ?? {};

    if (query.isLoading || _.isEmpty(originalTree)) {
      return;
    }
    const _tree = [_.cloneDeep(originalTree)];
    if (initialExpandedPath) {
      return _.map(_tree, TreeFuncs.map(updateExpandOnItems([...initialExpandedPath])));
    }

    return _tree;
  }, [initialExpandedPath, query.data, query.isLoading]);

  const breadcrumbPath = useMemo(() => {
    if (_.isEmpty(tree) || _.isEmpty(tree[0].children)) {
      return []; // Need a tree, wait until fetched
    }
    let item = nodeContext?.node;
    if (nodeContext?.node == null) {
      item = tree[0].children[0];
    }

    return nodePathNames(item);

  }, [tree, nodeContext?.node]);

  // Update selected item after tree is loaded
  useEffect(() => {
    if (_.isEmpty(tree)) {
      return;
    }
    let item, parent;

    if (!_.isEmpty(initialExpandedPath)) {
      const [lastParent, lastItem] = _.takeRight(initialExpandedPath, 2);

      item = lastItem;
      parent = lastParent;

    } else if (_.isEmpty(initialExpandedPath)) {
      item = tree[0].id;
      parent = null;
      onTreeRootChange?.(tree[0].id);
    }
    const selectedItem = TreeFuncs.findOne(findNode(parent, item), tree[0]);

    if (!_.isEmpty(selectedItem)) {
      onItemSelected?.(selectedItem);
      setActiveItems(_.compact([selectedItem]));
    } else {
      onItemSelected?.(tree[0]);
      setActiveItems(_.compact([tree[0]]));
    }

  }, [initialExpandedPath, onTreeRootChange, onItemSelected, tree, treeId]);

  useEffect(() => {
    if (_.isEmpty(treeRoots)) {
      return;
    }
    const selectedRoot = nodeContext?.lastTreeRoot;
    const selectedTreeRoot = selectedRoot ? _.find(treeRoots, { id: selectedRoot.id }) : null;
    const defaultRoot = selectedTreeRoot?.id ?? treeRoots[0].id;


    setInitialRoot(currentInitialRoot => {
      let newCurrentInitialRoot = currentInitialRoot;
      // If we have an initial root that we do not have in the list of roots, set it to the default root
      if (currentInitialRoot != null && treeRoots.find(root => root.id === currentInitialRoot) == null) {
        newCurrentInitialRoot = defaultRoot;
      }

      const newRoot = newCurrentInitialRoot ?? defaultRoot;
      setTreeId(newRoot);
      setQueryTreeId(newRoot);
      return newRoot;
    });
    // Ignore lastTreeRoot, this effect is only used for initial load of treeRoots
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeRoots]);

  const handleOnResize = (width) => {
    localStorage.setItem('drawerWidth', `${width}px`);
  };

  const _treeSelectItem = useCallback((item) => {
    onItemSelected?.(item);
    // trigger an on root change to save the new root
    onTreeRootChange?.(queryTreeId ?? initialRoot);
    if (isTabletOrMobile) {
      setIsDrawerExpanded(false);
    }
  }, [onItemSelected, onTreeRootChange, queryTreeId, initialRoot, isTabletOrMobile]);


  const selectTree = useCallback((selectedTreeId) => {
    setQueryTreeId(selectedTreeId);
    if (!onTreeRootChange) { // these should be updated through props if there is a connected function
      setTreeId(selectedTreeId);
      setInitialRoot(selectedTreeId);
    }
    onTreeRootChange?.(selectedTreeId);
  }, [onTreeRootChange]);

  const handleOnExpand = () => {
    drawerRef.current && drawerRef.current.focus();
  };

  return (
    <>
      { alerts }
      <Stack className={'node-tree-page-main'}>
        <StackItem>
          <Flex>
            <FlexItem>
              <Breadcrumb
                breadcrumbPath={breadcrumbPath}
                isExpanded={isDrawerExpanded}
                onExpand={setIsDrawerExpanded}
              />
            </FlexItem>
            <FlexItem align={{ default: 'alignRight' }}>
              <HelpButton file={help} />
            </FlexItem>
          </Flex>
        </StackItem>
        <StackItem isFilled className={'node-tree-page-content'}>
          <Drawer isExpanded={isDrawerExpanded} onExpand={handleOnExpand} isInline position="left">
            <DrawerContent panelContent={
              <DrawerPanelContent isResizable defaultSize={defaultDrawerWidth} minSize={'100px'} onResize={(_event, width) => handleOnResize(width)}>
                <div className={'tree-selector-and-tree'} tabIndex={isDrawerExpanded ? 0 : -1} ref={drawerRef} >
                  <Stack>
                    { panelTitle && <StackItem><FormLabel>{ panelTitle }</FormLabel></StackItem> }
                    <StackItem>
                      <TreeSelector
                        treeRoots={treeRoots}
                        initialRoot={queryTreeId}
                        isLoading={isLoadingRoots}
                        onSelect={selectTree}
                        onRefresh={refreshList}
                      />
                    </StackItem>
                    <StackItem className={'tree-container'} isFilled>
                      <Tree
                        tree={tree}
                        activeItems={activeItems}
                        onSelect={_treeSelectItem}
                        isLoading={query.isLoading}
                        nodeFilter={NODE_FILTER}
                      />
                    </StackItem>
                  </Stack>
                </div>
              </DrawerPanelContent>
            }>
              <DrawerContentBody className="drawer-content-body">
                { children }
              </DrawerContentBody>
            </DrawerContent>
          </Drawer>
        </StackItem>
      </Stack>
    </>
  );
};

NodeTreePage.propTypes = {
  children: PropTypes.node,
  onItemSelected: PropTypes.func,
  initialExpandedPath: PropTypes.array,
  initialRoot: PropTypes.string,
  help: PropTypes.string,
  panelTitle: PropTypes.string
};

export default React.memo(NodeTreePage);
